import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { Plus, Trash2, Save, CalendarX2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { Panel, Field, Spinner, btnPrimary, btnSecondary, btnGhost, inputCls, fmtDate } from "@/components/portal/ui";

const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const SLOT_OPTIONS = [15, 20, 30, 45, 60];
const DEFAULT_RANGE = { start: "09:00", end: "13:00" };

function RangeRow({ range, onChange, onRemove, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <input type="time" step={900} value={range.start} onChange={(e) => onChange({ ...range, start: e.target.value })} className={`${inputCls} !w-auto !py-1.5 !px-3`} data-testid={`${testid}-start`} />
      <span className="text-xs text-[#8A809A]">→</span>
      <input type="time" step={900} value={range.end} onChange={(e) => onChange({ ...range, end: e.target.value })} className={`${inputCls} !w-auto !py-1.5 !px-3`} data-testid={`${testid}-end`} />
      <button type="button" onClick={onRemove} aria-label="Rimuovi fascia" className="rounded-full p-1.5 text-[#8A809A] hover:bg-red-50 hover:text-red-600 transition-colors" data-testid={`${testid}-remove`}>
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function AvailabilityEditor({ endpoint, onSaved, readOnlyNote }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [excMode, setExcMode] = useState("off");
  const [excRanges, setExcRanges] = useState([{ ...DEFAULT_RANGE }]);

  useEffect(() => {
    setData(null);
    api.get(endpoint).then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
  }, [endpoint]);

  const excDates = useMemo(() => (data?.exceptions || []).map((e) => new Date(`${e.date}T12:00:00`)), [data]);

  const updateDay = (k, ranges) => setData((d) => ({ ...d, weekly: { ...d.weekly, [k]: ranges } }));

  const addException = () => {
    if (!selectedDate) return;
    const date = format(selectedDate, "yyyy-MM-dd");
    const entry = { date, available: excMode === "custom", ranges: excMode === "custom" ? excRanges : [] };
    setData((d) => ({ ...d, exceptions: [...d.exceptions.filter((e) => e.date !== date), entry].sort((a, b) => a.date.localeCompare(b.date)) }));
    setSelectedDate(undefined);
  };

  const removeException = (date) => setData((d) => ({ ...d, exceptions: d.exceptions.filter((e) => e.date !== date) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data: saved } = await api.put(endpoint, { slot_minutes: data.slot_minutes, weekly: data.weekly, exceptions: data.exceptions });
      setData(saved);
      toast.success("Disponibilità salvate");
      onSaved?.(saved);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (error) return <p className="text-sm text-red-600" data-testid="availability-error">{error}</p>;
  if (!data) return <div className="flex justify-center py-10"><Spinner /></div>;

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-5" data-testid="availability-editor">
      <Panel className="xl:col-span-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-heading text-xl font-semibold">Orari settimanali</h3>
            <p className="mt-1 text-sm text-[#6B6280]">Attiva i giorni e imposta le fasce in cui sei disponibile per le visite. Puoi modificarle in qualsiasi momento.</p>
          </div>
          <Field label="Durata visita">
            <select value={data.slot_minutes} onChange={(e) => setData({ ...data, slot_minutes: Number(e.target.value) })} className={`${inputCls} !w-auto !py-1.5`} data-testid="slot-minutes-select">
              {SLOT_OPTIONS.map((m) => (
                <option key={m} value={m}>{m} minuti</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="mt-5 divide-y divide-[#5B2C91]/8">
          {DAYS.map((label, i) => {
            const k = String(i);
            const ranges = data.weekly[k] || [];
            const on = ranges.length > 0;
            return (
              <div key={k} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start" data-testid={`weekday-row-${k}`}>
                <div className="flex w-36 shrink-0 items-center gap-3">
                  <Switch checked={on} onCheckedChange={(v) => updateDay(k, v ? [{ ...DEFAULT_RANGE }] : [])} className="data-[state=checked]:bg-[#5B2C91]" data-testid={`weekday-switch-${k}`} />
                  <span className={`text-sm font-semibold ${on ? "text-[#1F152E]" : "text-[#8A809A]"}`}>{label}</span>
                </div>
                <div className="flex flex-1 flex-col gap-2">
                  {on ? (
                    <>
                      {ranges.map((r, j) => (
                        <RangeRow
                          key={j}
                          range={r}
                          testid={`weekday-${k}-range-${j}`}
                          onChange={(nr) => updateDay(k, ranges.map((x, idx) => (idx === j ? nr : x)))}
                          onRemove={() => updateDay(k, ranges.filter((_, idx) => idx !== j))}
                        />
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const last = ranges[ranges.length - 1];
                          const nextStart = last?.end && last.end < "20:00" ? last.end : "15:00";
                          const [h] = nextStart.split(":").map(Number);
                          updateDay(k, [...ranges, { start: nextStart, end: `${String(Math.min(h + 3, 23)).padStart(2, "0")}:00` }]);
                        }}
                        className={`${btnGhost} self-start`}
                        data-testid={`weekday-${k}-add-range`}
                      >
                        <Plus className="h-3.5 w-3.5" /> Aggiungi fascia
                      </button>
                    </>
                  ) : (
                    <span className="py-1.5 text-sm text-[#8A809A]">Non disponibile</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Panel>

      <div className="flex flex-col gap-6 xl:col-span-2">
        <Panel>
          <h3 className="font-heading text-xl font-semibold">Eccezioni e giorni particolari</h3>
          <p className="mt-1 text-sm text-[#6B6280]">Seleziona una data per segnarla come non disponibile oppure per impostare orari diversi dal solito.</p>
          <div className="mt-4 flex justify-center rounded-2xl bg-[#F6F4FA] p-2">
            <Calendar
              mode="single"
              locale={it}
              selected={selectedDate}
              onSelect={setSelectedDate}
              disabled={{ before: new Date() }}
              modifiers={{ exception: excDates }}
              modifiersClassNames={{ exception: "bg-[#FF8A5B]/25 font-bold rounded-full" }}
              data-testid="exceptions-calendar"
            />
          </div>

          {selectedDate && (
            <div className="mt-4 rounded-2xl border border-[#5B2C91]/10 p-4" data-testid="exception-form">
              <p className="text-sm font-semibold">{format(selectedDate, "EEEE d MMMM yyyy", { locale: it })}</p>
              <div className="mt-3 flex gap-2">
                <button type="button" onClick={() => setExcMode("off")} className={`${excMode === "off" ? btnPrimary : btnSecondary} !px-4 !py-2 !text-xs`} data-testid="exception-mode-off">
                  <CalendarX2 className="h-3.5 w-3.5" /> Non disponibile
                </button>
                <button type="button" onClick={() => setExcMode("custom")} className={`${excMode === "custom" ? btnPrimary : btnSecondary} !px-4 !py-2 !text-xs`} data-testid="exception-mode-custom">
                  <CalendarClock className="h-3.5 w-3.5" /> Orari speciali
                </button>
              </div>
              {excMode === "custom" && (
                <div className="mt-3 flex flex-col gap-2">
                  {excRanges.map((r, j) => (
                    <RangeRow key={j} range={r} testid={`exception-range-${j}`} onChange={(nr) => setExcRanges(excRanges.map((x, idx) => (idx === j ? nr : x)))} onRemove={() => setExcRanges(excRanges.filter((_, idx) => idx !== j))} />
                  ))}
                  <button type="button" onClick={() => setExcRanges([...excRanges, { start: "15:00", end: "18:00" }])} className={`${btnGhost} self-start`} data-testid="exception-add-range">
                    <Plus className="h-3.5 w-3.5" /> Aggiungi fascia
                  </button>
                </div>
              )}
              <button type="button" onClick={addException} className={`${btnSecondary} mt-4 w-full`} data-testid="exception-add-btn">
                Aggiungi eccezione
              </button>
            </div>
          )}

          <ul className="mt-4 flex flex-col gap-2" data-testid="exceptions-list">
            {data.exceptions.length === 0 && <li className="text-sm text-[#8A809A]">Nessuna eccezione impostata.</li>}
            {data.exceptions.map((e) => (
              <li key={e.date} className="flex items-center justify-between gap-3 rounded-2xl bg-[#F6F4FA] px-4 py-2.5 text-sm" data-testid={`exception-item-${e.date}`}>
                <div>
                  <span className="font-semibold capitalize">{fmtDate(e.date)}</span>
                  <span className="ml-2 text-[#6B6280]">{e.available ? e.ranges.map((r) => `${r.start}–${r.end}`).join(", ") : "Non disponibile"}</span>
                </div>
                <button type="button" onClick={() => removeException(e.date)} aria-label="Rimuovi eccezione" className="rounded-full p-1.5 text-[#8A809A] hover:bg-red-50 hover:text-red-600" data-testid={`exception-remove-${e.date}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        </Panel>

        <div className="flex flex-col gap-2">
          <button type="button" onClick={save} disabled={saving} className={`${btnPrimary} w-full !py-3`} data-testid="availability-save-btn">
            <Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva disponibilità"}
          </button>
          {readOnlyNote && <p className="text-center text-xs text-[#8A809A]">{readOnlyNote}</p>}
        </div>
      </div>
    </div>
  );
}
